import { useEffect, useRef, useState } from 'react';
import EmojiPicker, { EmojiClickData, Theme, EmojiStyle } from 'emoji-picker-react';
import { Smile } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDarkMode } from '../../hooks/useDarkMode';

interface EmojiNameInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  maxLength?: number;
  size?: 'sm' | 'md';
  autoFocus?: boolean;
  placeholder?: string;
}

export function EmojiNameInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  maxLength = 50,
  size = 'md',
  autoFocus,
  placeholder,
}: EmojiNameInputProps) {
  const { t } = useTranslation();
  const { theme } = useDarkMode();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const showEmojiPickerRef = useRef(false);

  const minChars = size === 'sm' ? 6 : 8;
  const maxChars = size === 'sm' ? 20 : 28;
  const widthChars = Math.min(Math.max(value.length + 1, placeholder?.length ?? 0, minChars), maxChars);

  useEffect(() => {
    showEmojiPickerRef.current = showEmojiPicker;
  }, [showEmojiPicker]);

  const setPickerOpen = (open: boolean) => {
    showEmojiPickerRef.current = open;
    setShowEmojiPicker(open);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    if (!inputRef.current) return;
    const start = inputRef.current.selectionStart ?? 0;
    const end = inputRef.current.selectionEnd ?? 0;
    const next = value.slice(0, start) + emojiData.emoji + value.slice(end);
    if (next.length <= maxLength) {
      onChange(next);
      window.setTimeout(() => {
        const pos = start + emojiData.emoji.length;
        inputRef.current?.setSelectionRange(pos, pos);
        inputRef.current?.focus();
      }, 0);
    }
    setPickerOpen(false);
  };

  const keepInputFocus = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const handleInputBlur = () => {
    window.setTimeout(() => {
      const active = document.activeElement;
      if (containerRef.current?.contains(active)) return;
      if (showEmojiPickerRef.current) return;
      onBlur();
    }, 0);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      setPickerOpen(false);
    };
    if (!showEmojiPicker) return;
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  return (
    <div ref={containerRef} className="emoji-name-input relative inline-flex max-w-full min-w-0">
      <div
        className={`inline-flex max-w-full min-w-0 items-center rounded-lg border border-room-accent bg-room-surface-2 ${
          size === 'sm' ? 'text-[14.5px]' : 'text-[19px]'
        }`}
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={handleInputBlur}
          style={{ width: `${widthChars}ch` }}
          className={`min-w-0 bg-transparent py-1 pl-2 font-bold text-room-text outline-none ${
            size === 'sm'
              ? 'max-w-[min(calc(100vw-10rem),20ch)] text-[14.5px]'
              : 'max-w-[min(calc(100vw-10rem),28ch)] text-[19px]'
          }`}
          maxLength={maxLength}
          autoFocus={autoFocus}
          placeholder={placeholder}
        />
        <button
          type="button"
          onMouseDown={keepInputFocus}
          onClick={() => setPickerOpen(!showEmojiPicker)}
          className="emoji-button shrink-0 rounded-md p-1 text-room-text-faint transition-colors hover:bg-room-surface-3 hover:text-room-text-dim"
          title={t('header.addEmoji')}
          aria-label={t('header.addEmoji')}
        >
          <Smile size={size === 'sm' ? 15 : 16} />
        </button>
      </div>
      {showEmojiPicker ? (
        <div
          ref={emojiPickerRef}
          onMouseDown={keepInputFocus}
          className="absolute left-0 top-full z-[100] mt-2 max-w-[calc(100vw-2rem)] shadow-xl"
        >
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            theme={theme === 'dark' ? Theme.DARK : Theme.LIGHT}
            width={320}
            height={400}
            emojiStyle={EmojiStyle.APPLE}
          />
        </div>
      ) : null}
    </div>
  );
}
